import { create } from "zustand";
import type { Workshop } from "@/content/workshops";

/**
 * Capability-bubble ↔ workshop mapping (MC_00 ring, `c1`-`c6`) + the
 * cross-hover state that links them to the Program Rollup workshop rows.
 *
 * The mapping is a content decision (confirmed with Patrick 2026-08-21),
 * not derivable from either data source — capability bubbles and
 * workshops are independent lists with no shared key. WS8 (Flight Plan /
 * roadmap phases) doesn't map to any bubble's own content, so it's folded
 * into `c1` (Strategy & Delivery Model) alongside WS1.
 */
export const BUBBLE_WORKSHOPS: Record<string, Workshop["id"][]> = {
  c1: ["WS1", "WS8"], // Strategy & Delivery Model
  c2: ["WS2", "WS3"], // Process & Workflow
  c3: ["WS5"], // Governance & Accountability
  c4: ["WS6"], // Technology & Knowledge
  c5: ["WS4"], // Workforce & Culture
  c6: ["WS7"], // Performance & Value
};

/** Reverse lookup — workshop id → the one capability bubble it feeds. */
export const WORKSHOP_TO_BUBBLE: Record<string, string> = Object.entries(
  BUBBLE_WORKSHOPS,
).reduce((acc, [bubbleId, workshopIds]) => {
  for (const id of workshopIds) acc[id] = bubbleId;
  return acc;
}, {} as Record<string, string>);

type BubbleWorkshopHoverState = {
  /** Capability bubble currently hovered (MC_00 ring), or `null`. */
  hoveredBubbleId: string | null;
  /** Workshop row currently hovered (Program Rollup), or `null`. */
  hoveredWorkshopId: string | null;
  setHoveredBubble: (id: string | null) => void;
  setHoveredWorkshop: (id: string | null) => void;
};

/**
 * Global hover-sync store for the MC_00 capability ring ↔ Program Rollup
 * workshop list. Same shape/pattern as {@link useBubbleTooltip} — a
 * single global slice read by both sides, no prop drilling.
 */
export const useBubbleWorkshopLink = create<BubbleWorkshopHoverState>(
  (set) => ({
    hoveredBubbleId: null,
    hoveredWorkshopId: null,
    setHoveredBubble: (id) => set({ hoveredBubbleId: id }),
    setHoveredWorkshop: (id) => set({ hoveredWorkshopId: id }),
  }),
);
